import {getId} from 'apis/model/base'
import {LoginContext} from 'components/LoginContext'
import {useQueryString} from 'hooks'
import _ from 'lodash'
import {useHistory} from 'modules/navigation/useRouter'
import PermissionProvider from 'modules/permissions/PermissionProvider'
import React, {useCallback, useContext, useEffect, useState} from 'react'
import OrganizationContainerContext, {OrganizationContainerProvider,} from 'views/AppLocals/Organization/OrganizationContainerContext'
import {getRouteSearchParams, useRouteSearchParam} from "../../envs/_current/history";
import {ContentNotFound} from "../AppLocals/ContentNotFound";
import Pure from "../../components/Pure";
import {useRouterSearchParam} from "../../modules/navigation/useOnLocationChange";
import WorkspaceContext from "./WorkspaceContext";
import LoadingPage from "../LoadingPage";

export const WorkspaceFilterParams = Object.freeze({
    PERIOD: 'period',
})

export const SummaryFilterOptions = Object.freeze({
    TODAY: {
        label: 'today',
        value: 'TODAY',
    },
    YESTERDAY: {
        label: 'yesterday',
        value: 'YESTERDAY',
    },
    LAST7DAYS: {
        label: 'last 7 days',
        value: 'LAST7DAYS',
    },
    THISMONTH: {
        label: 'this month',
        value: 'THISMONTH',
    },
    LASTMONTH: {
        label: 'last month',
        value: 'LASTMONTH',
    },
    THISYEAR: {
        label: 'this year',
        value: 'THISYEAR',
    },
    LASTYEAR: {
        label: 'last year',
        value: 'LASTYEAR',
    },
    LAST3YEAR: {
        label: 'last 3 years',
        value: 'LAST3YEAR',
    },
})

const default_filter_params = {
    [WorkspaceFilterParams.PERIOD]: SummaryFilterOptions.THISMONTH.value,
}

export const WorkspaceProvider = ({children, ...props}) => {
    const {onChangeSearchParams} = useRouterSearchParam({});
    const history = useHistory()
    const [queriesObject] = useQueryString(_.get(history, 'location.pathname'))
    const [filter_params, set_filter_params] = useState()
    const {primary_org} = useContext(LoginContext)
    let orgIdParam = useRouteSearchParam("org_id");
    if (!orgIdParam) {
        orgIdParam = typeof primary_org === 'object' ? getId(primary_org) : primary_org;
    }
    const [selectedOrgId, setSelectedOrgId] = useState(orgIdParam);

    useEffect(() => {
        setSelectedOrgId(orgIdParam)
    }, [orgIdParam])


    useEffect(() => {
        const params = Object.values(WorkspaceFilterParams).reduce((prev, name) => {
            const _value = _.get(queriesObject, name)
            if (_value === default_filter_params[name]) {
                return prev
            } else {
                return {
                    ...prev,
                    [name]: _value,
                }
            }
        }, {})
        set_filter_params(params)
    }, [queriesObject])

    useEffect(() => {
        if (!selectedOrgId) return
        const _searchParamObj = Object.fromEntries(getRouteSearchParams().entries())
        if (!!!_searchParamObj?.org_id) {
            onChangeSearchParams({..._searchParamObj, org_id: selectedOrgId})
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOrgId])

    const getPeriod = useCallback(
        (name) => {
            if (_.has(default_filter_params, name)) {
                return _.get(filter_params, name, default_filter_params[name])
            } else {
                return null
            }
        },
        [filter_params]
    )

    const setPeriod = useCallback(
        (name, value) => {
            if (_.has(default_filter_params, name)) {
                const {search, pathname} = _.get(history, 'location', {})
                let _params = getRouteSearchParams()
                _params.set(name, value)
                history.push({
                    pathname,
                    search: ['?', _params.toString()].join(''),
                })
            }
        },
        [filter_params]
    )

    return (
        <Pure input={[selectedOrgId]}>
            <OrganizationContainerProvider item={selectedOrgId}>
                <OrganizationContainerContext.Consumer>
                    {({organization, isLoading}) => {
                        if (isLoading) {
                            return (
                                <LoadingPage/>
                            )
                        }
                        if (!organization?.edit) {
                            return (
                                <ContentNotFound/>
                            )
                        }
                        const permissions = _.get(organization, 'available_actions', [])
                        return (
                            <PermissionProvider permissions={permissions}>
                                <WorkspaceContext.Provider
                                    key={selectedOrgId}
                                    value={{
                                        ...props,
                                        getPeriod,
                                        setPeriod,
                                        organization,
                                        organization_id: selectedOrgId,
                                    }}>
                                    {children}
                                </WorkspaceContext.Provider>
                            </PermissionProvider>
                        )
                    }}
                </OrganizationContainerContext.Consumer>
            </OrganizationContainerProvider>
        </Pure>
    )
}

